import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../api/cachedDataHooks';
import { ConflictActions, OverwritableOptions, UploadFileOptions } from '../../utils/interfaces';
import { validationUtils } from '../../utils/validationFunctions';
import { getReplaceExistingUrl } from '../../utils/utils';
import { Routes } from '../../routes/routes';
import { useNavigation } from '../../utils/customHooks';
import { useTranslation } from '../../common/i18n/useTranslation';

type RequestFunction = () => Promise<void | Awaited<unknown>>;

/**
 * Options for saving the last request.
 * @property doUpdateData - Function to call for updating data.
 * @property isUploadFiles - Flag indicating if files are being uploaded.
 * @property showSuccessMessage - Whether to show a success message after retrying.
 * @property refetchFiles - Whether to refetch files after retrying.
 * @property navigateTo - Route to navigate to after retrying.
 * @property doUploadFile - Function to call for uploading files.
 */

export interface NextRequestOptions {
    doUpdateData?: RequestFunction;
    isUploadFiles?: boolean;
    onConflict?: ConflictActions;
    showSuccessMessage?: boolean;
    refetchFiles?: boolean;
    doUploadFile?: (uploadFileOptions: UploadFileOptions) => Promise<void>;
    navigateTo?: Routes;
}

/**
 * Context type for the retry request context.
 * @property isOverwriteDialogOpen - Indicates if the overwrite dialog is open.
 * @property openOverwriteDialog - Function to open the overwrite dialog.
 * @property closeOverwriteDialog - Function to close the overwrite dialog.
 * @property configureNextRequest - Function to save the last request with options.
 * @property retryLastRequest - Function to retry the last saved request.
 * @property showSuccessMessage - Indicates if a success message should be shown after retrying.
 * @property setLastQueryKey - Function to set the last query key.
 * @property retryLastGet - Function to retry the last GET request.
 * @property overwriteDialogMessagesAndOptions - Options for the overwrite dialog messages.
 * @property setOverwriteDialogMessagesAndOptions - Function to set the overwrite dialog messages and options.
 */
interface RetryRequestContextType {
    isOverwriteDialogOpen: boolean;
    openOverwriteDialog: () => void;
    closeOverwriteDialog: () => void;
    configureNextRequest: (options: NextRequestOptions) => void;
    retryLastRequest: () => Promise<void>;
    showSuccessMessage: boolean;
    setLastQueryKey: Dispatch<SetStateAction<QUERY_KEY | undefined>>;
    retryLastGet: () => Promise<void>;
    setOverwriteDialogMessagesAndOptions: Dispatch<SetStateAction<OverwritableOptions>>;
    overwriteDialogMessagesAndOptions: OverwritableOptions | undefined;
}

const RetryRequestContext = createContext<RetryRequestContextType | undefined>(undefined);

export function RetryRequestProvider(props: Readonly<{ children: React.ReactNode }>): React.ReactElement {
    const navigateTo = useNavigation();
    const translate = useTranslation();

    const [isOverwriteDialogOpen, setIsOverwriteDialogOpen] = useState(false);
    const [lastRequest, setLastRequest] = useState<RequestFunction | null>(null);
    const [lastQueryKey, setLastQueryKey] = useState<QUERY_KEY>();
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(true);
    const openOverwriteDialog = useCallback(() => setIsOverwriteDialogOpen(true), []);
    const closeOverwriteDialog = useCallback(() => setIsOverwriteDialogOpen(false), []);
    const [refetchFiles, setRefetchFiles] = useState<boolean>(false);
    const [doUploadFile, setDoUploadFile] =
        useState<(uploadFileOptions: UploadFileOptions) => Promise<void> | undefined>();
    const queryClient = useQueryClient();
    const [mainOverview, setMainOverview] = useState<Routes | undefined>(undefined);
    const [isUploadFiles, setIsUploadFiles] = useState<boolean>(false);
    const [onConflict, setOnConflict] = useState<ConflictActions | undefined>();

    const [overwriteDialogMessagesAndOptions, setOverwriteDialogMessagesAndOptions] = useState<OverwritableOptions>({
        dialogTitle: translate('conflict_when_saving'),
    });

    const configureNextRequest = useCallback((options: NextRequestOptions) => {
        setLastRequest(() => options.doUpdateData ?? null); // Save the last request function
        setShowSuccessMessage(options.showSuccessMessage ?? true);
        setRefetchFiles(options.refetchFiles ?? false);
        setOnConflict(options.onConflict);
        setDoUploadFile(() => options.doUploadFile);
        setMainOverview(options.navigateTo);
        setIsUploadFiles(options.isUploadFiles ?? false);
        //default message and show save button
        if (options.onConflict?.overwrite !== false) {
            setOverwriteDialogMessagesAndOptions({
                showSaveButton: true,
                dialogContentMessage: options.isUploadFiles
                    ? translate('file_conflict_when_saving_text')
                    : translate('data_conflict_when_saving_text'),
                doAfterSuccessfulRetry: options.onConflict?.doAfterSuccessfulUpload,
            });
        }
    }, []);

    const retryLastRequest = async () => {
        if (lastRequest || isUploadFiles) {
            try {
                if (lastRequest) {
                    await lastRequest();
                }
                if (!validationUtils.isArrayEmpty(onConflict?.uploadFilesOptions)) {
                    await uploadFiles();
                }

                if (lastQueryKey || refetchFiles) {
                    await queryClient.invalidateQueries({ queryKey: [lastQueryKey] }); //  Query refetch
                    await queryClient.invalidateQueries({ queryKey: ['FILES'], exact: false }); //  Query refetch for files if needed
                }

                if (onConflict?.refetch) {
                    await onConflict.refetch();
                }
            } catch (error) {
                console.error('Error occurred while retrying last request:', error);
                throw error; // Re-throw to handle in the dialog
            } finally {
                if (onConflict?.navigateBack !== false && mainOverview) {
                    await navigateTo(mainOverview);
                }
                cleanUp();
            }
        }
    };

    const retryLastGet = async () => {
        await queryClient.invalidateQueries({ queryKey: ['FILES'], exact: false });
        if (lastQueryKey) {
            await queryClient.invalidateQueries({ queryKey: [lastQueryKey] }); //  Query refetch
        }
        if (onConflict?.refetch) {
            await onConflict.refetch();
        } else if (!lastQueryKey && !refetchFiles) {
            //fallback if no queryKey was set.
            window.location.reload();
        }
        cleanUp();
    };

    const uploadFiles = async () => {
        if (validationUtils.isArrayEmpty(onConflict?.uploadFilesOptions) || !doUploadFile) {
            return;
        }

        try {
            for (const option of onConflict?.uploadFilesOptions ?? []) {
                // Skip, if no files or no queryString
                if (validationUtils.isArrayEmpty(option.files) || !option.queryString) {
                    continue;
                }

                const queryStringExistingUrl = getReplaceExistingUrl(option.queryString);

                await doUploadFile({
                    apiUrl: queryStringExistingUrl,
                    files: option.files ?? [],
                    method: option.method ?? 'POST',
                });
            }
        } catch (error) {
            console.error('Error occurred while uploading files during retry:', error);
            throw error; // Re-throw to handle in the dialog
        }
    };

    const cleanUp = () => {
        setLastRequest(null); // Clear last request after retry
        setLastQueryKey(undefined);
        setIsOverwriteDialogOpen(false); // Close dialog after retry
        setRefetchFiles(false);
        setShowSuccessMessage(false); // Reset success message flag
        setOnConflict(undefined);
        setDoUploadFile(undefined);
        setMainOverview(undefined);
        setIsUploadFiles(false);
        setOverwriteDialogMessagesAndOptions({});
    };

    const providerValue = useMemo(
        () => ({
            isOverwriteDialogOpen,
            openOverwriteDialog,
            closeOverwriteDialog,
            configureNextRequest,
            retryLastRequest,
            showSuccessMessage,
            setLastQueryKey,
            retryLastGet,
            setOverwriteDialogMessagesAndOptions,
            overwriteDialogMessagesAndOptions,
        }),
        [isOverwriteDialogOpen, showSuccessMessage, lastRequest, retryLastGet, overwriteDialogMessagesAndOptions]
    );

    return <RetryRequestContext.Provider value={providerValue}>{props.children}</RetryRequestContext.Provider>;
}

export const useRetryRequestContext = () => {
    const context = useContext(RetryRequestContext);
    if (!context) {
        throw new Error('useRetryRequestContext must be used within a RetryRequestProvider');
    }
    return context;
};
